import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { faqs } from '@/mocks/comics';
import { useState } from 'react';

const siteUrl = 'https://www.hanman.com';
const pageTitle = '关于我们 - 韩漫网-免费韩国漫画大全';
const pageDesc = '韩漫网致力于为全球中文读者提供海量正版韩国漫画免费在线阅读服务。了解我们的品牌故事、平台优势及联系方式。';
const pageKeywords = '韩漫网,关于我们,韩国漫画,免费漫画平台,联系我们';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: pageTitle,
  description: pageDesc,
  url: `${siteUrl}/about`,
  isPartOf: {
    '@type': 'WebSite',
    name: '韩漫网',
    url: siteUrl,
  },
  about: {
    '@type': 'Organization',
    name: '韩漫网',
    description: pageDesc,
    url: siteUrl,
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@hanman.com',
      contactType: 'customer service',
      availableLanguage: ['Chinese'],
    },
  },
};

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: f.answer,
    },
  })),
};

const milestones = [
  { year: '2018', title: '平台创立', desc: '韩漫网正式上线，专注为中文读者提供高质量韩国漫画阅读服务。' },
  { year: '2019', title: '资源扩充', desc: '漫画资源突破1000部，与多家韩国知名漫画平台达成合作。' },
  { year: '2020', title: '用户突破', desc: '月活跃用户突破500万，成为中文圈最受欢迎的韩漫阅读平台之一。' },
  { year: '2022', title: '技术升级', desc: '全面升级阅读引擎，支持高清画质与多设备无缝切换阅读体验。' },
  { year: '2024', title: '内容扩展', desc: '漫画资源突破5000部，涵盖恋爱、奇幻、动作等十大热门分类。' },
  { year: '2026', title: '行业领先', desc: '月活跃用户突破2000万，成为全球最大的中文韩漫阅读平台。' },
];

const stats = [
  { value: '5000+', label: '漫画资源' },
  { value: '2000万+', label: '月活跃用户' },
  { value: '10万+', label: '每日更新章节' },
  { value: '99.7%', label: '用户满意度' },
];

const teamMembers = [
  {
    name: '李明浩',
    role: '创始人 & CEO',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20male%20CEO%20portrait%2C%20modern%20office%20background%2C%20friendly%20smile%2C%20clean%20professional%20photography%2C%20warm%20lighting&width=200&height=200&seq=team-01&orientation=squarish',
  },
  {
    name: '朴智慧',
    role: '内容总监',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20female%20executive%20portrait%2C%20modern%20office%20background%2C%20confident%20expression%2C%20clean%20professional%20photography%2C%20warm%20lighting&width=200&height=200&seq=team-02&orientation=squarish',
  },
  {
    name: '金泰勋',
    role: '技术负责人',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20male%20engineer%20portrait%2C%20modern%20office%20background%2C%20focused%20expression%2C%20clean%20professional%20photography%2C%20warm%20lighting&width=200&height=200&seq=team-03&orientation=squarish',
  },
  {
    name: '崔秀英',
    role: '运营总监',
    avatar: 'https://readdy.ai/api/search-image?query=Professional%20Korean%20female%20manager%20portrait%2C%20modern%20office%20background%2C%20warm%20smile%2C%20clean%20professional%20photography%2C%20warm%20lighting&width=200&height=200&seq=team-04&orientation=squarish',
  },
];

export default function AboutPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={pageKeywords}
        canonicalUrl={`${siteUrl}/about`}
        schemaData={{ ...aboutSchema, ...faqSchema }}
      />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6">
          <Breadcrumb items={[{ label: '关于我们' }]} />

          <section className="py-10 md:py-14">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>
                关于韩漫网
              </h1>
              <p className="text-foreground-500 leading-relaxed text-sm md:text-base">
                韩漫网成立于2018年，是专注于为全球中文读者提供正版韩国漫画免费在线阅读的平台。
                我们始终坚持"让每一部好漫画被看到"的初心，为用户打造最佳的漫画阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 md:gap-6 mb-16">
              {stats.map((stat) => (
                <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100">
                  <div className="text-2xl md:text-3xl font-bold text-primary-500 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
                    {stat.value}
                  </div>
                  <div className="text-xs text-foreground-500">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100/60 -mx-4 md:-mx-6 px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              发展历程
            </h2>
            <div className="max-w-3xl mx-auto relative">
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-background-200/70 md:-translate-x-0.5"></div>
              {milestones.map((item, index) => (
                <div
                  key={item.year}
                  className={`relative flex items-start gap-6 mb-8 md:mb-10 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'} ${index % 2 === 0 ? 'pl-12 md:pl-0' : 'pl-12 md:pl-0'}`}>
                    <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 inline-block max-w-md">
                      <span className="text-xs font-bold text-primary-500">{item.year}</span>
                      <h3 className="text-base font-semibold text-foreground-950 mt-1 mb-1">{item.title}</h3>
                      <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                  <div className="absolute left-[10px] md:left-1/2 md:-translate-x-1.5 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 z-10 top-1.5"></div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-2 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              我们的团队
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-10">一群热爱漫画的人，用心打造最好的漫画平台</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {teamMembers.map((member) => (
                <div key={member.name} className="text-center">
                  <div className="w-20 h-20 mx-auto rounded-full overflow-hidden bg-background-100 mb-3">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  <h3 className="text-sm font-semibold text-foreground-950">{member.name}</h3>
                  <p className="text-xs text-foreground-500 mt-0.5">{member.role}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16 bg-background-100/60 -mx-4 md:-mx-6 px-4 md:px-6">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              常见问题
            </h2>
            <div className="max-w-2xl mx-auto flex flex-col gap-3">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-4 text-left cursor-pointer"
                  >
                    <span className="text-sm font-medium text-foreground-800 pr-4">{faq.question}</span>
                    <i
                      className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 flex-shrink-0 ${
                        openFaq === index ? 'rotate-180' : ''
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      openFaq === index ? 'max-h-[300px] pb-4 px-4' : 'max-h-0'
                    }`}
                  >
                    <p className="text-sm text-foreground-500 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="py-12 md:py-16">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-10 text-center" style={{ fontFamily: 'var(--font-heading)' }}>
              联系我们
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto">
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-mail-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮件</h3>
                <p className="text-xs text-foreground-500">contact@hanman.com</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-customer-service-2-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">在线客服</h3>
                <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-100">
                <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-500 mb-3">
                  <i className="ri-map-pin-line text-xl"></i>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                <p className="text-xs text-foreground-500">韩国首尔特别市江南区</p>
              </div>
            </div>
          </section>
        </div>
      </main>
      <Footer />
      <ScrollToTop />
    </>
  );
}